import { env } from "../config/env";
import {
  demoAnalytics,
  demoCampaignRequests,
  demoCampaigns,
  demoDelay,
  demoMe,
  demoNotifications,
  demoProducts,
  demoRequestsByCampaign,
} from "../demo/demo-data";
import type {
  AnalyticsAiReply,
  AnalyticsResponse,
  AuthSession,
  Campaign,
  CampaignRequestsResponse,
  JsonRecord,
  MeResponse,
  NotificationItem,
  Product,
  RegionOption,
} from "../types/supplier";
import { apiRequest } from "./http";

const items = <T>(value: { items?: T[] }) => value.items ?? [];

const liveSupplierApi = {
  signInWithPhone(firebaseIdToken: string) {
    return apiRequest<AuthSession & JsonRecord>("/v1/auth/supplier/phone", {
      method: "POST",
      auth: false,
      body: {
        firebaseIdToken,
        signupSource: "supplier_app",
        terms: {
          serviceTermsAgreed: true,
          privacyPolicyAgreed: true,
          agreedAt: new Date().toISOString(),
        },
      },
    });
  },

  recoverWithdrawal(firebaseIdToken: string) {
    return apiRequest<AuthSession>("/v1/auth/supplier/recover-withdrawal", {
      method: "POST",
      auth: false,
      body: {
        firebaseIdToken,
        signupSource: "supplier_app",
        terms: {
          serviceTermsAgreed: true,
          privacyPolicyAgreed: true,
          agreedAt: new Date().toISOString(),
        },
      },
    });
  },

  refresh(refreshToken: string) {
    return apiRequest<AuthSession>("/v1/auth/refresh", {
      method: "POST",
      auth: false,
      body: { refreshToken },
    });
  },

  logout(refreshToken: string) {
    return apiRequest<JsonRecord>("/v1/auth/logout", {
      method: "POST",
      auth: false,
      body: { refreshToken },
    });
  },

  me: () => apiRequest<MeResponse>("/v1/me"),
  deleteMe: () => apiRequest<JsonRecord>("/v1/me", { method: "DELETE" }),

  completeOnboarding(input: {
    contactName: string;
    contactEmail: string;
    companyName: string;
    businessNumber: string;
    representativeName: string;
    openingDate: string;
    businessAddress: string;
  }) {
    return apiRequest<JsonRecord>("/v1/supplier/onboarding", {
      method: "POST",
      body: {
        contact: {
          contactName: input.contactName,
          contactEmail: input.contactEmail,
        },
        company: {
          companyName: input.companyName,
          businessRegistrationNumber: input.businessNumber,
          representativeName: input.representativeName,
          openingDate: input.openingDate,
          businessAddress: input.businessAddress,
          productCategories: [],
        },
      },
    });
  },

  updateProfile(input: JsonRecord) {
    return apiRequest<JsonRecord>("/v1/supplier/profile", {
      method: "PATCH",
      body: input,
    });
  },

  regions: (level?: number, parentCode?: string) =>
    apiRequest<{ items?: RegionOption[] }>("/v1/regions", {
      auth: false,
      query: { level, parentCode },
    }).then(items),

  notifications: () =>
    apiRequest<{ items?: NotificationItem[] }>("/v1/notifications").then(items),
  unreadNotificationCount: () =>
    apiRequest<{ unreadCount?: number }>("/v1/notifications/unread-count").then(
      (data) => data.unreadCount ?? 0,
    ),
  markNotificationRead: (id: string) =>
    apiRequest<JsonRecord>(`/v1/notifications/${id}/read`, {
      method: "PATCH",
      body: {},
    }),
  registerPushToken: (token: string) =>
    apiRequest<JsonRecord>("/v1/push-tokens", {
      method: "POST",
      body: { appType: "supplier_app", platform: "web", token },
    }),

  products: () =>
    apiRequest<{ items?: Product[] }>("/v1/supplier/products").then(items),
  createProduct: (input: JsonRecord) =>
    apiRequest<{ product: Product }>("/v1/supplier/products", {
      method: "POST",
      body: input,
    }),
  createCampaign: (input: JsonRecord) =>
    apiRequest<{ sampleCampaign: Campaign }>(
      "/v1/supplier/sample-campaigns",
      { method: "POST", body: input },
    ),
  campaigns: () =>
    apiRequest<{ items?: Campaign[] }>("/v1/supplier/sample-campaigns").then(
      items,
    ),
  campaignRequests: (campaignId: string) =>
    apiRequest<CampaignRequestsResponse>(
      `/v1/supplier/sample-campaigns/${campaignId}/requests`,
    ),
  updateCampaign: (campaignId: string, input: JsonRecord) =>
    apiRequest<{ sampleCampaign: Campaign }>(
      `/v1/supplier/sample-campaigns/${campaignId}`,
      { method: "PATCH", body: input },
    ),
  selectFullProductReward: (
    requestId: string,
    selected: boolean,
    note?: string,
  ) =>
    apiRequest<JsonRecord>(
      `/v1/supplier/sample-requests/${requestId}/full-product-reward`,
      {
        method: "POST",
        body: { selected, ...(note?.trim() ? { note: note.trim() } : {}) },
      },
    ),
  updateProduct: (productId: string, input: JsonRecord) =>
    apiRequest<JsonRecord>(`/v1/supplier/products/${productId}`, {
      method: "PATCH",
      body: input,
    }),
  saveProductDraft: (draft: JsonRecord) =>
    apiRequest<JsonRecord>("/v1/supplier/product-draft", {
      method: "PUT",
      body: { draft },
    }),
  productDraft: () =>
    apiRequest<{ draft?: JsonRecord }>("/v1/supplier/product-draft"),
  clearProductDraft: () =>
    apiRequest<JsonRecord>("/v1/supplier/product-draft", {
      method: "DELETE",
    }),
  analytics: (params: {
    dateFrom: string;
    dateTo: string;
    productId?: string;
  }) =>
    apiRequest<AnalyticsResponse>("/v1/supplier/analytics", {
      query: params,
    }),
  analyticsUsers: (params: {
    dateFrom: string;
    dateTo: string;
    productId: string;
    offset?: number;
    limit?: number;
  }) =>
    apiRequest<JsonRecord>("/v1/supplier/analytics/users", {
      query: params,
    }),
  chatAnalyticsAi: (input: {
    productId: string;
    context: JsonRecord;
    message: string;
    sessionId?: string;
  }) =>
    apiRequest<AnalyticsAiReply>("/v1/supplier/analytics/ai/chat", {
      method: "POST",
      baseUrl: env.analyticsAiUrl,
      body: input,
    }),

  uploadImage(file: File, purpose: string) {
    return apiRequest<{ file?: { publicUrl?: string } }>("/v1/files", {
      method: "POST",
      headers: {
        "content-type": file.type,
        "x-file-name": encodeURIComponent(file.name),
        "x-file-purpose": purpose,
      },
      body: file,
    }).then((data) => {
      const url = data.file?.publicUrl;
      if (!url) throw new Error("이미지 업로드 응답이 올바르지 않습니다.");
      return url;
    });
  },
};

const demoSupplierApi: typeof liveSupplierApi = {
  ...liveSupplierApi,
  signInWithPhone: () =>
    demoDelay({ accessToken: "demo-access", refreshToken: "demo-refresh" }),
  recoverWithdrawal: () =>
    demoDelay({ accessToken: "demo-access", refreshToken: "demo-refresh" }),
  refresh: () =>
    demoDelay({ accessToken: "demo-access", refreshToken: "demo-refresh" }),
  logout: () => demoDelay({}),
  me: () => demoDelay(demoMe),
  deleteMe: () => demoDelay({}),
  completeOnboarding: () => demoDelay({}),
  updateProfile: (input) => demoDelay(input),
  regions: (level, parentCode) => {
    const options: RegionOption[] =
      level === 1
        ? [
            {
              code: "11",
              name: "서울특별시",
              fullName: "서울특별시",
              level: 1,
              sidoCode: "11",
              sidoName: "서울특별시",
            },
            {
              code: "41",
              name: "경기도",
              fullName: "경기도",
              level: 1,
              sidoCode: "41",
              sidoName: "경기도",
            },
          ]
        : level === 2 && parentCode === "11"
          ? [
              {
                code: "11680",
                name: "강남구",
                fullName: "서울특별시 강남구",
                level: 2,
                sidoCode: "11",
                sidoName: "서울특별시",
                sigunguCode: "11680",
                sigunguName: "강남구",
              },
              {
                code: "11440",
                name: "마포구",
                fullName: "서울특별시 마포구",
                level: 2,
                sidoCode: "11",
                sidoName: "서울특별시",
                sigunguCode: "11440",
                sigunguName: "마포구",
              },
            ]
          : level === 2
            ? [
                {
                  code: "41135",
                  name: "성남시",
                  fullName: "경기도 성남시",
                  level: 2,
                  sidoCode: "41",
                  sidoName: "경기도",
                  sigunguCode: "41135",
                  sigunguName: "성남시",
                },
              ]
            : [
                {
                  code: "1168010100",
                  name: "역삼동",
                  fullName: "서울특별시 강남구 역삼동",
                  level: 3,
                  sidoCode: "11",
                  sidoName: "서울특별시",
                  sigunguCode: parentCode ?? "11680",
                  sigunguName: "강남구",
                  bcode: "1168010100",
                  bname: "역삼동",
                },
              ];
    return demoDelay(options);
  },
  notifications: () => demoDelay(demoNotifications),
  unreadNotificationCount: () =>
    demoDelay(demoNotifications.filter((item) => !item.isRead).length),
  markNotificationRead: (id) => {
    const item = demoNotifications.find((notification) => notification.id === id);
    if (item) item.isRead = true;
    return demoDelay({});
  },
  registerPushToken: () => demoDelay({}),
  products: () => demoDelay([...demoProducts]),
  createProduct: (input) => {
    const product: Product = {
      id: `demo-product-${Date.now()}`,
      name: String(input.name ?? "새 상품"),
      status: "pending",
      categories: Array.isArray(input.categories)
        ? input.categories.filter(
            (value): value is string => typeof value === "string",
          )
        : [],
      brandName:
        typeof input.brandName === "string" ? input.brandName : undefined,
      mainImageUrl:
        typeof input.mainImageUrl === "string" ? input.mainImageUrl : undefined,
      shortDescription:
        typeof input.shortDescription === "string"
          ? input.shortDescription
          : undefined,
      survey:
        input.survey && typeof input.survey === "object"
          ? (input.survey as JsonRecord)
          : undefined,
      createdAt: new Date().toISOString(),
    };
    demoProducts.unshift(product);
    return demoDelay({ product });
  },
  createCampaign: (input) => {
    const campaign: Campaign = {
      id: `demo-campaign-${Date.now()}`,
      productId: String(input.productId ?? ""),
      status: "pending",
      totalQuantity: Number(input.totalQuantity ?? 0),
      requestedCount: 0,
      availableQuantity: Number(input.totalQuantity ?? 0),
      receiptPhotoRequired: input.receiptPhotoRequired === true,
      fullProductRewardEnabled: input.fullProductRewardEnabled === true,
      fullProductRewardCount: Number(input.fullProductRewardCount ?? 0),
      regionScope:
        input.regionScope && typeof input.regionScope === "object"
          ? (input.regionScope as Campaign["regionScope"])
          : { type: "nationwide", label: "전국" },
      startsAt: typeof input.startsAt === "string" ? input.startsAt : undefined,
    };
    demoCampaigns.unshift(campaign);
    demoRequestsByCampaign[campaign.id] = [];
    return demoDelay({ sampleCampaign: campaign });
  },
  campaigns: () => demoDelay([...demoCampaigns]),
  campaignRequests: (campaignId) =>
    demoDelay(demoCampaignRequests(campaignId)),
  updateCampaign: (campaignId, input) => {
    const campaign =
      demoCampaigns.find((item) => item.id === campaignId) ?? demoCampaigns[0];
    Object.assign(campaign, input);
    return demoDelay({ sampleCampaign: campaign });
  },
  selectFullProductReward: (requestId, selected, note) => {
    for (const requests of Object.values(demoRequestsByCampaign)) {
      const request = requests.find((item) => item.id === requestId);
      if (request) {
        request.fullProductRewardSelected = selected;
        request.fullProductRewardNote = note;
      }
    }
    return demoDelay({});
  },
  updateProduct: (productId, input) => {
    const product = demoProducts.find((item) => item.id === productId);
    if (product) Object.assign(product, input);
    return demoDelay({ product });
  },
  saveProductDraft: (draft) => demoDelay({ draft }),
  productDraft: () => demoDelay({}),
  clearProductDraft: () => demoDelay({}),
  analytics: ({ productId }) =>
    demoDelay({
      ...demoAnalytics,
      items: productId
        ? demoAnalytics.items.filter((item) => item.productId === productId)
        : demoAnalytics.items,
    }),
  analyticsUsers: ({ productId, offset = 0, limit = 50 }) => {
    const product = demoProducts.find((item) => item.id === productId);
    const allItems = Array.from({ length: 24 }, (_, index) => ({
      productId,
      productName: product?.name ?? "상품",
      userLabel: `익명 사용자 ${String(index + 1).padStart(3, "0")}`,
      hasSampleRequest: index % 4 !== 0,
      latestSampleRequestStatus:
        index % 3 === 0 ? "survey_completed" : "delivered",
      actualSampleRequestCount: index % 4 === 0 ? 0 : 1,
      eventCount: 8 + index,
      productViewCount: 2 + (index % 8),
      beforeRequestViewCount: 1 + (index % 4),
      afterRequestViewCount: index % 5,
      afterDeliveryViewCount: index % 3,
      purchaseClickCount: index % 4,
      shareClickCount: index % 3,
      sampleApplyClickCount: index % 4 === 0 ? 0 : 1,
      sampleRequestCount: index % 4 === 0 ? 0 : 1,
      avgViewDurationMs: 38_000 + index * 1_700,
    }));
    return demoDelay({
      items: allItems.slice(offset, offset + limit),
      total: allItems.length,
      offset,
      limit,
    });
  },
  chatAnalyticsAi: ({ message }) =>
    demoDelay({
      answer: `현재 캠페인은 상품 조회에서 샘플 신청으로 이어지는 전환이 안정적입니다. "${message}" 관점에서는 배송 완료 후 사용자 경험 응답을 높이기 위해 수령 다음 날 리마인드 알림과 응답 혜택 안내를 강화하는 것이 우선입니다.`,
      sessionId: "demo-ai-session",
      responseId: `demo-${Date.now()}`,
    }, 500),
  uploadImage: (file) => demoDelay(URL.createObjectURL(file)),
};

export const supplierApi = env.demoMode ? demoSupplierApi : liveSupplierApi;
